
import * as React from 'react';
import { getCurrentItemSetKey } from '../decorators/selectors';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IActionCommandBarOwnProps, ActionCommandBar, IActionCommandBarProps } from '../actionCommandBar/ActionCommandBar';
import { itemsCommandBarControl } from '../../controls/itemsCommandBarControl';
import { ICommand } from '../../models/commands/ICommand';
import { newCommand } from '../commands/new/newCommand';
import { openCommand } from '../commands/open/openCommand';
import { uploadCommand } from '../commands/upload/uploadCommand';
import { shareCommand, copyLinkCommand } from '../commands/share/shareCommand';
import { downloadCommand } from '../commands/download/downloadCommand';
import { renameCommand } from '../commands/rename/renameCommand';
import { deleteCommand } from '../commands/delete/deleteCommand';
import { showProgressCommand } from '../commands/showProgress/showProgressCommand';
import { switchLayoutCommand } from '../commands/switchLayout/switchLayoutCommand';
import { clearSelectionCommand } from '../commands/clearSelection/clearSelectionCommand';
import { IDecoratedProps } from '../../utilities/components/Decorators';
import { concat } from '../../utilities/arrays';
import { layoutCommand } from '../commands/layout/layoutCommand';
import { wrapWithParent } from '../../utilities/components/Props';
import { createTelemetryProvider } from '../telemetry/TelemetryProvider';
import { IEventContext } from '@ms/telemetry-manager/lib/IEvent';
import { withPageLoad, IWithPageLoadProps } from '../decorators/afterPageLoad';
import { followCommand } from '../commands/follow/followCommand';
import { getCurrentItemKey } from '../../models/itemsScope/getCurrentItemKey';

const DEFAULT_COMMANDS = [
  newCommand,
  uploadCommand,
  openCommand,
  shareCommand,
  copyLinkCommand,
  downloadCommand,
  followCommand,
  deleteCommand,
  renameCommand
];

const DEFAULT_FAR_COMMANDS = [
  clearSelectionCommand,
  showProgressCommand,
  switchLayoutCommand,
  layoutCommand
];

const DEFAULT_OVERFLOW_COMMANDS: ICommand[] = [];

const DEFAULT_SHORTCUT_COMMANDS: ICommand[] = [];

const NO_COMMANDS: ICommand[] = [];

const COMMAND_BAR_TELEMETRY_CONTEXT: IEventContext = {
  component: 'CommandBar'
};

const CommandBarTelemetryProvider = createTelemetryProvider({
  withContext: COMMAND_BAR_TELEMETRY_CONTEXT
});

/**
 * @public
 */
export type IItemsCommandBarInjectedProps = Pick<IActionCommandBarOwnProps,
  'commands' | 'farCommands' | 'overflowCommands' | 'shortcutCommands'>;

/**
 * @public
 */
export type IItemsCommandBarOwnProps = IItemsCommandBarInjectedProps;

/**
 * @public
 */
export type IItemsCommandBarProps = IDecoratedProps<
  IActionCommandBarProps,
  IItemsCommandBarInjectedProps, IItemsCommandBarOwnProps>;

/**
 * Component which provides a command bar with standard commands for the current item and
 * selected items.
 * @public
 */
export const ItemsCommandBar: React.ComponentType<IItemsCommandBarProps> = wrapWithParent(CommandBarTelemetryProvider)(
  withPageLoad()(
    connectToItemCache(mapCurrentItemSetToCommandBarProps)(ActionCommandBar)
  )
);

ItemsCommandBar.displayName = 'ItemsCommandBar';

function mapCurrentItemSetToCommandBarProps(
  itemCache: IItemCache,
  props: IItemsCommandBarOwnProps & IWithPageLoadProps
): IItemsCommandBarInjectedProps {
  const itemKey = getCurrentItemKey(itemCache);
  const itemSetKey = getCurrentItemSetKey(itemCache);

  const {
    commands: moreCommands = NO_COMMANDS,
    overflowCommands: moreOverflowCommands = NO_COMMANDS,
    farCommands: moreFarCommands = NO_COMMANDS,
    shortcutCommands: moreShortcutCommands = NO_COMMANDS
  } = props;

  if (props.hasPageLoaded) {
    const controlHandler = itemCache.demandItemFacet(itemsCommandBarControl, itemKey);

    const {
      commands,
      overflowCommands,
      farCommands,
      shortcutCommands
    } = itemsCommandBarControl.evaluate(controlHandler)(itemCache, {
      itemKey: itemKey || '',
      itemSetKey: itemSetKey,
      commands: DEFAULT_COMMANDS,
      farCommands: DEFAULT_FAR_COMMANDS,
      overflowCommands: DEFAULT_OVERFLOW_COMMANDS,
      shortcutCommands: DEFAULT_SHORTCUT_COMMANDS
    });

    return {
      commands: concat(commands, moreCommands) as ICommand[],
      overflowCommands: concat(overflowCommands, moreOverflowCommands) as ICommand[],
      farCommands: concat(farCommands, moreFarCommands) as ICommand[],
      shortcutCommands: concat(shortcutCommands, moreShortcutCommands) as ICommand[]
    };
  } else {
    return {};
  }
}
